import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// TYPES
// ============================================================================

export type PopoverPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

export type PopoverTrigger = 'click' | 'hover' | 'focus' | 'manual';

// ============================================================================
// POSITIONING UTILITIES
// ============================================================================

interface Position {
  top: number;
  left: number;
}

function calculatePosition(
  triggerRect: DOMRect,
  popoverRect: DOMRect,
  placement: PopoverPlacement,
  offset: number = 8
): Position {
  let top = 0;
  let left = 0;

  switch (placement) {
    case 'top':
      top = triggerRect.top - popoverRect.height - offset;
      left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
      break;
    case 'top-start':
      top = triggerRect.top - popoverRect.height - offset;
      left = triggerRect.left;
      break;
    case 'top-end':
      top = triggerRect.top - popoverRect.height - offset;
      left = triggerRect.right - popoverRect.width;
      break;
    case 'bottom':
      top = triggerRect.bottom + offset;
      left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
      break;
    case 'bottom-start':
      top = triggerRect.bottom + offset;
      left = triggerRect.left;
      break;
    case 'bottom-end':
      top = triggerRect.bottom + offset;
      left = triggerRect.right - popoverRect.width;
      break;
    case 'left':
      top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
      left = triggerRect.left - popoverRect.width - offset;
      break;
    case 'left-start':
      top = triggerRect.top;
      left = triggerRect.left - popoverRect.width - offset;
      break;
    case 'left-end':
      top = triggerRect.bottom - popoverRect.height;
      left = triggerRect.left - popoverRect.width - offset;
      break;
    case 'right':
      top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
      left = triggerRect.right + offset;
      break;
    case 'right-start':
      top = triggerRect.top;
      left = triggerRect.right + offset;
      break;
    case 'right-end':
      top = triggerRect.bottom - popoverRect.height;
      left = triggerRect.right + offset;
      break;
  }

  return { top, left };
}

function getArrowStyles(placement: PopoverPlacement): React.CSSProperties {
  const arrowSize = 8;
  const baseStyles: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
  };

  if (placement.startsWith('top')) {
    return {
      ...baseStyles,
      bottom: -arrowSize,
      borderLeft: `${arrowSize}px solid transparent`,
      borderRight: `${arrowSize}px solid transparent`,
      borderTop: `${arrowSize}px solid currentColor`,
      left: placement === 'top' ? '50%' : placement === 'top-start' ? '16px' : undefined,
      right: placement === 'top-end' ? '16px' : undefined,
      transform: placement === 'top' ? 'translateX(-50%)' : undefined,
    };
  }

  if (placement.startsWith('bottom')) {
    return {
      ...baseStyles,
      top: -arrowSize,
      borderLeft: `${arrowSize}px solid transparent`,
      borderRight: `${arrowSize}px solid transparent`,
      borderBottom: `${arrowSize}px solid currentColor`,
      left: placement === 'bottom' ? '50%' : placement === 'bottom-start' ? '16px' : undefined,
      right: placement === 'bottom-end' ? '16px' : undefined,
      transform: placement === 'bottom' ? 'translateX(-50%)' : undefined,
    };
  }

  if (placement.startsWith('left')) {
    return {
      ...baseStyles,
      right: -arrowSize,
      borderTop: `${arrowSize}px solid transparent`,
      borderBottom: `${arrowSize}px solid transparent`,
      borderLeft: `${arrowSize}px solid currentColor`,
      top: placement === 'left' ? '50%' : placement === 'left-start' ? '12px' : undefined,
      bottom: placement === 'left-end' ? '12px' : undefined,
      transform: placement === 'left' ? 'translateY(-50%)' : undefined,
    };
  }

  if (placement.startsWith('right')) {
    return {
      ...baseStyles,
      left: -arrowSize,
      borderTop: `${arrowSize}px solid transparent`,
      borderBottom: `${arrowSize}px solid transparent`,
      borderRight: `${arrowSize}px solid currentColor`,
      top: placement === 'right' ? '50%' : placement === 'right-start' ? '12px' : undefined,
      bottom: placement === 'right-end' ? '12px' : undefined,
      transform: placement === 'right' ? 'translateY(-50%)' : undefined,
    };
  }

  return baseStyles;
}

// ============================================================================
// POPOVER CONTENT
// ============================================================================

export interface PopoverContentProps {
  children: React.ReactNode;
  className?: string;
}

export function PopoverContent({ children, className }: PopoverContentProps) {
  return <div className={clsx('p-3', className)}>{children}</div>;
}

// ============================================================================
// POPOVER HEADER
// ============================================================================

export interface PopoverHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function PopoverHeader({ children, className }: PopoverHeaderProps) {
  return (
    <div
      className={clsx(
        'px-3 py-2 border-b border-gray-200 dark:border-gray-700 font-medium text-gray-900 dark:text-white',
        className
      )}
    >
      {children}
    </div>
  );
}

// ============================================================================
// POPOVER BODY
// ============================================================================

export interface PopoverBodyProps {
  children: React.ReactNode;
  className?: string;
}

export function PopoverBody({ children, className }: PopoverBodyProps) {
  return <div className={clsx('p-3', className)}>{children}</div>;
}

// ============================================================================
// POPOVER FOOTER
// ============================================================================

export interface PopoverFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function PopoverFooter({ children, className }: PopoverFooterProps) {
  return (
    <div
      className={clsx(
        'px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50',
        className
      )}
    >
      {children}
    </div>
  );
}

// ============================================================================
// POPOVER
// ============================================================================

export interface PopoverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: PopoverPlacement;
  trigger?: PopoverTrigger;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  offset?: number;
  showArrow?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  hoverDelay?: number;
  className?: string;
  contentClassName?: string;
}

export function Popover({
  children,
  content,
  placement = 'bottom',
  trigger = 'click',
  isOpen: controlledIsOpen,
  onOpenChange,
  offset = 8,
  showArrow = true,
  closeOnClickOutside = true,
  closeOnEscape = true,
  hoverDelay = 200,
  className,
  contentClassName,
}: PopoverProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = React.useState(false);
  const [position, setPosition] = React.useState<Position>({ top: 0, left: 0 });
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout>();

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;

  const setIsOpen = React.useCallback(
    (value: boolean) => {
      if (!isControlled) {
        setUncontrolledIsOpen(value);
      }
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange]
  );

  const updatePosition = React.useCallback(() => {
    if (triggerRef.current && popoverRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const newPosition = calculatePosition(triggerRect, popoverRect, placement, offset);

      // Adjust for viewport boundaries
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (newPosition.left < 0) newPosition.left = 8;
      if (newPosition.left + popoverRect.width > viewportWidth) {
        newPosition.left = viewportWidth - popoverRect.width - 8;
      }
      if (newPosition.top < 0) newPosition.top = 8;
      if (newPosition.top + popoverRect.height > viewportHeight) {
        newPosition.top = viewportHeight - popoverRect.height - 8;
      }

      setPosition(newPosition);
    }
  }, [placement, offset]);

  // Update position when open
  React.useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  // Click outside
  React.useEffect(() => {
    if (isOpen && closeOnClickOutside) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          triggerRef.current &&
          !triggerRef.current.contains(event.target as Node) &&
          popoverRef.current &&
          !popoverRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, closeOnClickOutside, setIsOpen]);

  // Escape key
  React.useEffect(() => {
    if (isOpen && closeOnEscape) {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeOnEscape, setIsOpen]);

  // Cleanup hover timeout
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    if (trigger === 'click') {
      setIsOpen(!isOpen);
    }
  };

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setIsOpen(true);
      }, hoverDelay);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, hoverDelay);
    }
  };

  const handleFocus = () => {
    if (trigger === 'focus') {
      setIsOpen(true);
    }
  };

  const handleBlur = () => {
    if (trigger === 'focus') {
      setIsOpen(false);
    }
  };

  return (
    <div className={clsx('relative inline-block', className)}>
      <div
        ref={triggerRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="inline-block"
      >
        {children}
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          className={clsx(
            'fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700',
            'animate-in fade-in-0 zoom-in-95 duration-100',
            contentClassName
          )}
          style={{
            top: position.top,
            left: position.left,
          }}
          onMouseEnter={trigger === 'hover' ? handleMouseEnter : undefined}
          onMouseLeave={trigger === 'hover' ? handleMouseLeave : undefined}
          role="dialog"
          aria-modal="false"
        >
          {showArrow && (
            <div
              className="text-white dark:text-gray-800"
              style={getArrowStyles(placement)}
              aria-hidden="true"
            />
          )}
          {content}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// POPOVER CLOSE BUTTON
// ============================================================================

export interface PopoverCloseProps {
  onClose: () => void;
  className?: string;
}

export function PopoverClose({ onClose, className }: PopoverCloseProps) {
  return (
    <button
      type="button"
      onClick={onClose}
      className={clsx(
        'absolute top-2 right-2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500',
        className
      )}
      aria-label="Close"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

// ============================================================================
// CONFIRMATION POPOVER
// ============================================================================

export interface ConfirmationPopoverProps {
  children: React.ReactNode;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: 'default' | 'danger';
  placement?: PopoverPlacement;
  className?: string;
}

export function ConfirmationPopover({
  children,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
  placement = 'top',
  className,
}: ConfirmationPopoverProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleConfirm = () => {
    onConfirm();
    setIsOpen(false);
  };

  const handleCancel = () => {
    onCancel?.();
    setIsOpen(false);
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement={placement}
      trigger="click"
      className={className}
      content={
        <div className="p-4 max-w-xs">
          <h4 className="font-medium text-gray-900 dark:text-white mb-1">{title}</h4>
          {message && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{message}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className={clsx(
                'px-3 py-1.5 text-sm rounded text-white',
                variant === 'danger'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              )}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      }
    >
      {children}
    </Popover>
  );
}

// ============================================================================
// INFO POPOVER
// ============================================================================

export interface InfoPopoverProps {
  children: React.ReactNode;
  title?: string;
  content: React.ReactNode;
  placement?: PopoverPlacement;
  className?: string;
}

export function InfoPopover({
  children,
  title,
  content,
  placement = 'top',
  className,
}: InfoPopoverProps) {
  return (
    <Popover
      placement={placement}
      trigger="hover"
      className={className}
      content={
        <div className="p-3 max-w-xs">
          {title && (
            <h4 className="font-medium text-gray-900 dark:text-white mb-1 text-sm">
              {title}
            </h4>
          )}
          <div className="text-sm text-gray-600 dark:text-gray-400">{content}</div>
        </div>
      }
    >
      {children}
    </Popover>
  );
}

// ============================================================================
// POPOVER MENU
// ============================================================================

export interface PopoverMenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
  onClick?: () => void;
}

export interface PopoverMenuProps {
  children: React.ReactNode;
  items: PopoverMenuItem[];
  placement?: PopoverPlacement;
  onSelect?: (item: PopoverMenuItem) => void;
  className?: string;
}

export function PopoverMenu({
  children,
  items,
  placement = 'bottom-start',
  onSelect,
  className,
}: PopoverMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleItemClick = (item: PopoverMenuItem) => {
    if (!item.disabled) {
      item.onClick?.();
      onSelect?.(item);
      setIsOpen(false);
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement={placement}
      trigger="click"
      showArrow={false}
      className={className}
      content={
        <div className="py-1 min-w-[160px]">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
              className={clsx(
                'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                item.disabled
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : item.danger
                  ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
            >
              {item.icon && <span className="w-4 h-4">{item.icon}</span>}
              {item.label}
            </button>
          ))}
        </div>
      }
    >
      {children}
    </Popover>
  );
}

// ============================================================================
// USE POPOVER HOOK
// ============================================================================

export interface UsePopoverOptions {
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export interface UsePopoverReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setIsOpen: (value: boolean) => void;
}

export function usePopover(options: UsePopoverOptions = {}): UsePopoverReturn {
  const { defaultOpen = false, onOpenChange } = options;
  const [isOpen, setIsOpenState] = React.useState(defaultOpen);

  const setIsOpen = React.useCallback(
    (value: boolean) => {
      setIsOpenState(value);
      onOpenChange?.(value);
    },
    [onOpenChange]
  );

  const open = React.useCallback(() => setIsOpen(true), [setIsOpen]);
  const close = React.useCallback(() => setIsOpen(false), [setIsOpen]);
  const toggle = React.useCallback(() => setIsOpen(!isOpen), [isOpen, setIsOpen]);

  return { isOpen, open, close, toggle, setIsOpen };
}
